import React, { useEffect, useState } from "react";
import HRHeader from "../components/HRHeader";
import api from "../api";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { useAuth } from "../context/AuthContext";
import { TYPE_LABELS, STATUS } from "./FeriePage";
import { TRIP_STATUS, euro } from "./TrasfertePage";
import { openFile } from "../api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Check, X, ClipboardCheck, Plane, CalendarDays, Paperclip, Laptop, Timer, RotateCcw } from "lucide-react";
import { notifyApprovalsChanged } from "../hooks/usePendingApprovals";
import InteractiveTour from "../components/InteractiveTour";
import { GuidaRapidaButton } from "../components/GuidaRapidaButton";
import { usePageTour } from "../hooks/usePageTour";
import { approvazioniTour } from "../miniTours";

const fmt = (iso) => { try { return format(new Date(iso), "dd MMM yyyy", { locale: it }); } catch { return iso; } };

/* ---------------- Leave approvals ---------------- */
const LeaveApprovals = () => {
  const [leaves, setLeaves] = useState([]);
  const [filter, setFilter] = useState("in_attesa");
  const [decision, setDecision] = useState(null);
  const [note, setNote] = useState("");

  const load = () => {
    const q = filter === "tutte" ? "" : `?status=${filter}`;
    api.get(`/leaves${q}`).then((r) => setLeaves(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const confirm = async () => {
    const { leave, action } = decision;
    const okMsg = { approve: "Richiesta approvata", reject: "Richiesta rifiutata", revoke: "Approvazione revocata, saldo ripristinato" };
    try {
      await api.put(`/leaves/${leave.id}/${action}`, { note });
      toast.success(okMsg[action] || "Fatto");
      notifyApprovalsChanged();
      setDecision(null); setNote(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="text-[14px] text-gray-500">{leaves.length} richieste</div>
        <div className="w-[200px]">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="leave-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="in_attesa" data-testid="leave-filter-in_attesa">In attesa</SelectItem>
              <SelectItem value="approvata" data-testid="leave-filter-approvata">Approvate</SelectItem>
              <SelectItem value="rifiutata" data-testid="leave-filter-rifiutata">Rifiutate</SelectItem>
              <SelectItem value="tutte" data-testid="leave-filter-tutte">Tutte</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {leaves.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400">
          <CalendarDays size={40} className="mx-auto mb-3 opacity-40" /> Nessuna richiesta
        </div>
      ) : (
        <div className="space-y-3">
          {leaves.map((lv) => (
            <div key={lv.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-[14px] font-bold text-[#16345c]">{lv.employee_name}</span>
                  <span className="text-[11px] text-gray-400">({lv.matricola})</span>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS[lv.status]?.cls}`}>{STATUS[lv.status]?.label}</span>
                </div>
                <div className="text-[13px] text-gray-700 font-semibold">{TYPE_LABELS[lv.type] || lv.type}</div>
                <div className="text-[13px] text-gray-600">
                  {lv.type === "permesso" ? `${fmt(lv.start_date)} · ${lv.hours} ore` : `${fmt(lv.start_date)} → ${fmt(lv.end_date)} · ${lv.days_count} gg`}
                </div>
                {lv.reason && <div className="text-[12.5px] text-gray-400 mt-1 italic">"{lv.reason}"</div>}
                {lv.decision_note && <div className="text-[12px] text-gray-500 mt-1"><b>{lv.approver_name}:</b> {lv.decision_note}</div>}
              </div>
              {lv.status === "in_attesa" && lv.can_act && (
                <div className="flex flex-col gap-2 shrink-0">
                  <button onClick={() => { setDecision({ leave: lv, action: "approve" }); setNote(""); }} data-testid={`leave-approve-${lv.id}`} className="flex items-center gap-1 bg-[#009640] hover:bg-[#007a34] text-white text-[12px] font-bold px-3 py-1.5 rounded-sm transition-colors"><Check size={14} /> Approva</button>
                  <button onClick={() => { setDecision({ leave: lv, action: "reject" }); setNote(""); }} data-testid={`leave-reject-${lv.id}`} className="flex items-center gap-1 bg-white border border-red-300 text-[#c00510] hover:bg-red-50 text-[12px] font-bold px-3 py-1.5 rounded-sm transition-colors"><X size={14} /> Rifiuta</button>
                </div>
              )}
              {lv.status === "in_attesa" && !lv.can_act && (
                <span className="text-[11px] font-semibold text-gray-400 shrink-0 self-center" data-testid="readonly-badge" title="Solo il responsabile del reparto può approvare">Sola lettura</span>
              )}
              {lv.status === "approvata" && lv.can_act && (
                <div className="flex flex-col gap-2 shrink-0">
                  <button onClick={() => { setDecision({ leave: lv, action: "revoke" }); setNote(""); }} data-testid={`leave-revoke-${lv.id}`} className="flex items-center gap-1 bg-white border border-orange-300 text-orange-700 hover:bg-orange-50 text-[12px] font-bold px-3 py-1.5 rounded-sm transition-colors"><RotateCcw size={14} /> Revoca</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{decision?.action === "approve" ? "Approva richiesta" : decision?.action === "revoke" ? "Revoca approvazione" : "Rifiuta richiesta"}</DialogTitle></DialogHeader>
          <p className="text-[14px] text-gray-600">{decision?.leave.employee_name} · {TYPE_LABELS[decision?.leave.type]}</p>
          {decision?.action === "revoke" && <p className="text-[13px] text-orange-700 bg-orange-50 border border-orange-200 rounded-sm px-3 py-2">L'approvazione verrà annullata e il saldo (giorni/ore) verrà ri-accreditato automaticamente al dipendente.</p>}
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Nota (facoltativa)..." data-testid="leave-decision-note" className="w-full border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c] resize-none" />
          <DialogFooter>
            <button onClick={() => setDecision(null)} className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-100 rounded-sm transition-colors">Annulla</button>
            <button onClick={confirm} data-testid="leave-decision-confirm" className={`px-4 py-2 text-[13px] font-bold text-white rounded-sm transition-colors ${decision?.action === "approve" ? "bg-[#009640] hover:bg-[#007a34]" : decision?.action === "revoke" ? "bg-orange-600 hover:bg-orange-700" : "bg-[#e30613] hover:bg-[#c00510]"}`}>Conferma</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ---------------- Trip approvals ---------------- */
const TripApprovals = () => {
  const { isAdmin } = useAuth();
  const [trips, setTrips] = useState([]);
  const [filter, setFilter] = useState(isAdmin ? "da_approvare" : "in_attesa_responsabile");
  const [decision, setDecision] = useState(null);
  const [note, setNote] = useState("");

  const load = () => {
    if (filter === "da_approvare") {
      // Admin: tutte le trasferte ancora da approvare (sia attesa responsabile sia attesa HR),
      // così non resta nessuna richiesta "nascosta" nel conteggio.
      api.get(`/trips`).then((r) => setTrips((r.data || []).filter((t) => t.status === "in_attesa_responsabile" || t.status === "in_attesa_hr"))).catch(() => {});
      return;
    }
    const q = filter === "tutte" ? "" : `?status=${filter}`;
    api.get(`/trips${q}`).then((r) => setTrips(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const confirm = async () => {
    const { trip, action } = decision;
    const endpoint = trip.status === "in_attesa_responsabile" ? "manager_decision" : "hr_decision";
    try {
      await api.put(`/trips/${trip.id}/${endpoint}`, { action, note });
      toast.success(action === "approve" ? "Trasferta approvata" : "Trasferta rifiutata");
      notifyApprovalsChanged();
      setDecision(null); setNote(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const canAct = (t) => !!t.can_act && (t.status === "in_attesa_responsabile" || t.status === "in_attesa_hr");

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="text-[14px] text-gray-500">{trips.length} trasferte</div>
        <div className="w-[220px]">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {!isAdmin && <SelectItem value="in_attesa_responsabile">Da approvare (responsabile)</SelectItem>}
              {isAdmin && <SelectItem value="da_approvare">Da approvare (tutte)</SelectItem>}
              {isAdmin && <SelectItem value="in_attesa_hr">Attesa HR</SelectItem>}
              {isAdmin && <SelectItem value="in_attesa_responsabile">Attesa responsabile</SelectItem>}
              <SelectItem value="approvata">Approvate</SelectItem>
              <SelectItem value="rifiutata">Rifiutate</SelectItem>
              <SelectItem value="tutte">Tutte</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {trips.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400">
          <Plane size={40} className="mx-auto mb-3 opacity-40" /> Nessuna trasferta
        </div>
      ) : (
        <div className="space-y-3">
          {trips.map((t) => (
            <div key={t.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-[14px] font-bold text-[#16345c]">{t.employee_name}</span>
                  <span className="text-[11px] text-gray-400">({t.matricola})</span>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${TRIP_STATUS[t.status]?.cls}`}>{TRIP_STATUS[t.status]?.label}</span>
                </div>
                <div className="text-[13px] text-gray-700 font-semibold">{t.destination}</div>
                <div className="text-[13px] text-gray-600">{fmt(t.start_date)} → {fmt(t.end_date)} · <b>{euro(t.total_expenses ?? t.estimated_amount)}</b></div>
                {t.reason && <div className="text-[12.5px] text-gray-400 mt-1 italic">"{t.reason}"</div>}
                {t.expenses?.length > 0 && (
                  <div className="mt-2 space-y-0.5">
                    {t.expenses.map((ex, i) => (
                      <div key={`${t.id}-exp-${i}`} className="flex items-center justify-between text-[12px] text-gray-600">
                        <span className="truncate">
                          <span className="inline-block text-[10px] font-bold bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded mr-1">{ex.category}</span>
                          {ex.description || "—"}
                          {ex.receipt_file_id && (
                            <button onClick={() => openFile(ex.receipt_file_id)} className="ml-1 text-[#0d6cb8] hover:underline inline-flex items-center gap-0.5"><Paperclip size={11} /> allegato</button>
                          )}
                        </span>
                        <span className="font-semibold text-gray-700 shrink-0 ml-2">{euro(ex.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {t.manager_decision && <div className="text-[12px] text-gray-500 mt-1"><b>Responsabile ({t.manager_decision.by}):</b> {t.manager_decision.note || "—"}</div>}
                {t.hr_decision && <div className="text-[12px] text-gray-500"><b>HR ({t.hr_decision.by}):</b> {t.hr_decision.note || "—"}</div>}
              </div>
              {canAct(t) && (
                <div className="flex flex-col gap-2 shrink-0">
                  <button onClick={() => { setDecision({ trip: t, action: "approve" }); setNote(""); }} className="flex items-center gap-1 bg-[#009640] hover:bg-[#007a34] text-white text-[12px] font-bold px-3 py-1.5 rounded-sm transition-colors"><Check size={14} /> Approva</button>
                  <button onClick={() => { setDecision({ trip: t, action: "reject" }); setNote(""); }} className="flex items-center gap-1 bg-white border border-red-300 text-[#c00510] hover:bg-red-50 text-[12px] font-bold px-3 py-1.5 rounded-sm transition-colors"><X size={14} /> Rifiuta</button>
                </div>
              )}
              {!t.can_act && (t.status === "in_attesa_responsabile" || t.status === "in_attesa_hr") && (
                <span className="text-[11px] font-semibold text-gray-400 shrink-0 self-center" data-testid="readonly-badge" title="Solo il responsabile del reparto può approvare">Sola lettura</span>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{decision?.action === "approve" ? "Approva trasferta" : "Rifiuta trasferta"}</DialogTitle></DialogHeader>
          <p className="text-[14px] text-gray-600">{decision?.trip.employee_name} · {decision?.trip.destination}</p>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Nota (facoltativa)..." className="w-full border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c] resize-none" />
          <DialogFooter>
            <button onClick={() => setDecision(null)} className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-100 rounded-sm transition-colors">Annulla</button>
            <button onClick={confirm} className={`px-4 py-2 text-[13px] font-bold text-white rounded-sm transition-colors ${decision?.action === "approve" ? "bg-[#009640] hover:bg-[#007a34]" : "bg-[#e30613] hover:bg-[#c00510]"}`}>Conferma</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ---------------- Simple request approvals (Smart Working / Straordinari) ---------------- */
const SimpleApprovals = ({ resource, icon: Icon, emptyLabel, renderMeta }) => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("in_attesa");
  const [decision, setDecision] = useState(null);
  const [note, setNote] = useState("");

  const load = () => {
    const q = filter === "tutte" ? "" : `?status=${filter}`;
    api.get(`/${resource}${q}`).then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const confirm = async () => {
    const { item, action } = decision;
    try {
      await api.put(`/${resource}/${item.id}/${action}`, { note });
      toast.success(action === "approve" ? "Richiesta approvata" : "Richiesta rifiutata");
      notifyApprovalsChanged();
      setDecision(null); setNote(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="text-[14px] text-gray-500">{items.length} richieste</div>
        <div className="w-[200px]">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="in_attesa">In attesa</SelectItem>
              <SelectItem value="approvata">Approvate</SelectItem>
              <SelectItem value="rifiutata">Rifiutate</SelectItem>
              <SelectItem value="tutte">Tutte</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400">
          <Icon size={40} className="mx-auto mb-3 opacity-40" /> {emptyLabel}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-[14px] font-bold text-[#16345c]">{it.employee_name}</span>
                  <span className="text-[11px] text-gray-400">({it.matricola})</span>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS[it.status]?.cls}`}>{STATUS[it.status]?.label}</span>
                </div>
                {renderMeta(it)}
                {it.decision_note && <div className="text-[12px] text-gray-500 mt-1"><b>{it.approver_name}:</b> {it.decision_note}</div>}
              </div>
              {it.status === "in_attesa" && it.can_act && (
                <div className="flex flex-col gap-2 shrink-0">
                  <button onClick={() => { setDecision({ item: it, action: "approve" }); setNote(""); }} className="flex items-center gap-1 bg-[#009640] hover:bg-[#007a34] text-white text-[12px] font-bold px-3 py-1.5 rounded-sm transition-colors"><Check size={14} /> Approva</button>
                  <button onClick={() => { setDecision({ item: it, action: "reject" }); setNote(""); }} className="flex items-center gap-1 bg-white border border-red-300 text-[#c00510] hover:bg-red-50 text-[12px] font-bold px-3 py-1.5 rounded-sm transition-colors"><X size={14} /> Rifiuta</button>
                </div>
              )}
              {it.status === "in_attesa" && !it.can_act && (
                <span className="text-[11px] font-semibold text-gray-400 shrink-0 self-center" data-testid="readonly-badge" title="Solo il responsabile del reparto può approvare">Sola lettura</span>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{decision?.action === "approve" ? "Approva richiesta" : "Rifiuta richiesta"}</DialogTitle></DialogHeader>
          <p className="text-[14px] text-gray-600">{decision?.item.employee_name}</p>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Nota (facoltativa)..." className="w-full border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c] resize-none" />
          <DialogFooter>
            <button onClick={() => setDecision(null)} className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-100 rounded-sm transition-colors">Annulla</button>
            <button onClick={confirm} className={`px-4 py-2 text-[13px] font-bold text-white rounded-sm transition-colors ${decision?.action === "approve" ? "bg-[#009640] hover:bg-[#007a34]" : "bg-[#e30613] hover:bg-[#c00510]"}`}>Conferma</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ApprovalsPage = () => {
  const tour = usePageTour("approvazioni");
  return (
    <div className="min-h-screen bg-[#f0f2f5]">
      <HRHeader />
      <main className="max-w-[1200px] mx-auto px-4 py-8">
        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2"><ClipboardCheck size={24} /> Approvazioni</h1>
            <p className="text-[14px] text-gray-500">Approva o rifiuta le richieste del tuo team</p>
          </div>
          <GuidaRapidaButton onClick={tour.open} />
        </div>
        <Tabs defaultValue="leaves">
          <TabsList data-testid="approvals-tabs" className="mb-6 h-auto w-full md:w-auto max-w-full justify-start flex-nowrap overflow-x-auto no-scrollbar">
            <TabsTrigger value="leaves" className="flex items-center gap-2"><CalendarDays size={15} /> Ferie e Permessi</TabsTrigger>
            <TabsTrigger value="trips" className="flex items-center gap-2"><Plane size={15} /> Trasferte</TabsTrigger>
            <TabsTrigger value="smartworking" className="flex items-center gap-2"><Laptop size={15} /> Smart Working</TabsTrigger>
            <TabsTrigger value="overtime" className="flex items-center gap-2"><Timer size={15} /> Straordinari</TabsTrigger>
          </TabsList>
          <TabsContent value="leaves"><LeaveApprovals /></TabsContent>
          <TabsContent value="trips"><TripApprovals /></TabsContent>
          <TabsContent value="smartworking">
            <SimpleApprovals resource="smartworking" icon={Laptop} emptyLabel="Nessuna richiesta di smart working"
              renderMeta={(it) => (
                <>
                  <div className="text-[13px] text-gray-700 font-semibold">{it.days_count} {it.days_count === 1 ? "giornata" : "giornate"} da remoto</div>
                  <div className="flex flex-wrap gap-1 mt-1">{(it.dates || []).map((d) => <span key={d} className="text-[12px] text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">{fmt(d)}</span>)}</div>
                  {it.note && <div className="text-[12.5px] text-gray-400 mt-1 italic">"{it.note}"</div>}
                </>
              )} />
          </TabsContent>
          <TabsContent value="overtime">
            <SimpleApprovals resource="overtime" icon={Timer} emptyLabel="Nessuno straordinario da approvare"
              renderMeta={(it) => (
                <>
                  <div className="text-[13px] text-gray-700 font-semibold">{it.hours} ore · {fmt(it.date)}</div>
                  {it.reason && <div className="text-[12.5px] text-gray-400 mt-1 italic">"{it.reason}"</div>}
                </>
              )} />
          </TabsContent>
        </Tabs>
      </main>
      {tour.show && <InteractiveTour steps={approvazioniTour} requireAck={false} onClose={tour.close} />}
    </div>
  );
};

export default ApprovalsPage;
